import { useState } from "react";
import { taskService } from "@miplatform/api";
import { EMAIL_TYPE, HTTP_STATUSES } from "@miplatform/constants";
import { useComponentState } from "@miplatform/hooks";
import { addToBusinessDate, getDate, isEmpty } from "@miplatform/utils";
import { useGlobalManagerApproval } from "../../../../../../components/GlobalManagerApprovalProvider/GlobalManagerApprovalProvider";
import { attachEventTime } from "../../../../utils";

export const useTaskCardActionModal = ({
  toastService,
  onAttemptUpdated = () => {},
  onEmailModalOpen = () => {},
  closeModal = () => {},
  attempt = {},
}) => {
  const { addressLine1: address } = attempt?.home?.address || {};

  const [cancelReasons, setCancelReasons] = useState([]);
  const [cannotPerformReasons, setCannotPerformReasons] = useState([]);

  const stateManager = useComponentState();

  const { executeApiRequest } = useGlobalManagerApproval();

  const handleStartWork = () => {
    closeModal();

    stateManager.run(async () => {
      const res = await taskService.attempts.start({
        data: { attemptId: attempt.id },
        modifiedOn: attempt.modifiedOn,
        toastMessageConfig: {
          success: {
            text: "Attempt Started",
            subtext: `${address} has started`,
          },
        },
      });

      onAttemptUpdated(res);
    });
  };

  const handleDispatchWork = () => {
    closeModal();

    stateManager.run(async () => {
      const options = await taskService.attempts.getDispatchOptions({
        data: { attemptId: attempt.id },
        modifiedOn: attempt.modifiedOn,
      });

      await executeApiRequest({
        options,
        toastMessageForManagerApproval: {
          text: "Open order limit exceeded. Manager approval required.",
          subtext:
            "The open order limit has been exceeded for this user and manager approval is required to dispatch the attempt.",
        },
        successToastMessageForManagerApproval: {
          text: "Attempt Dispatched",
          subtext: `${address} was dispatched`,
        },
        callback: ({ status, data }) => {
          if (status === HTTP_STATUSES.OK) {
            onAttemptUpdated(data);
            onEmailModalOpen({
              attempt: attachEventTime(data),
              type: EMAIL_TYPE.DISPATCH,
            });
          }
        },
      });
    });
  };

  const handleStop = () => {
    closeModal();

    stateManager.run(async () => {
      const res = await taskService.attempts.stop({
        data: {
          attemptId: attempt.id,
        },
        modifiedOn: attempt.modifiedOn,
      });

      if (res) {
        toastService.success({
          text: "Attempt Stopped",
          subtext: `${address} has stopped`,
        });

        onAttemptUpdated(res);
      }
    });
  };

  const handleUnschedule = () => {
    stateManager.run(async () => {
      const options = await taskService.attempts.unscheduleOptions({
        attemptId: attempt.id,
        modifiedOn: attempt.modifiedOn,
      });

      await executeApiRequest({
        options,
        toastMessageForManagerApproval: {
          text: "Order is locked. Manager approval needed.",
          subtext:
            "The order is locked and needs manager authorization before it can be moved.",
        },
        successToastMessageForManagerApproval: {
          text: "Attempt Unscheduled",
          subtext: `${address} was unscheduled`,
        },
        callback: ({ status, data }) => {
          if (status === HTTP_STATUSES.OK) {
            onAttemptUpdated(data);
            closeModal();
          }
        },
      });
    });
  };

  const handleLock = (types) => {
    closeModal();

    const lockTypes = Object.keys(types).filter((key) => types[key]);
    stateManager.run(async () => {
      const response = await taskService.attempts.lock({
        attemptId: attempt.id,
        data: {
          lockTypes,
          reason: "Manually Locked.",
        },
      });

      if (response) {
        toastService.success({
          text: `${attempt.assignment.order.orderTemplateName} at ${attempt.home.address.addressLine1} has been locked.`,
        });
      }
    });
  };

  const handleSubmitWork = ({ submitStatus, reason }) => {
    closeModal();

    stateManager.run(async () => {
      const res = await taskService.attempts.submit({
        data: { attemptId: attempt.id, intention: submitStatus, reason },
        modifiedOn: attempt.modifiedOn,
        toastMessageConfig: {
          success: {
            text: "Attempt Submitted",
            subtext: `${address} was submitted`,
          },
        },
      });

      onAttemptUpdated(res);
    });
  };

  const handleHold = ({
    contactName,
    calendarId,
    reason,
    updateCalendarCounts,
    holdUnit,
    holdValue,
  }) => {
    const followUpDateTime = addToBusinessDate(getDate(), holdValue, holdUnit);

    stateManager.run(async () => {
      const options = await taskService.order.getHoldOptions({
        orderId: attempt.orderId,
        data: {
          contactName,
          modifiedOn: attempt.orderModifiedOn,
          calendarId,
          followUpDate: followUpDateTime,
          reason,
        },
      });

      await executeApiRequest({
        options,
        callback: ({ status, data }) => {
          if (status === HTTP_STATUSES.OK) {
            toastService.success({
              text: `Order Placed on Hold for ${holdValue} ${holdUnit}`,
              subtext: `The Order has been placed on hold for ${holdValue} ${holdUnit.toLowerCase()}. It will move to the "Past Due" bucket after ${holdValue} ${holdUnit.toLowerCase()}.`,
            });

            data.assignments.forEach((assignment) => {
              assignment.attempts.forEach((attempt) => {
                onAttemptUpdated(attempt);
              });
            });

            updateCalendarCounts();
            closeModal();
          }
        },
      });
    });
  };

  const handleCancel = async ({ reason }) => {
    closeModal();

    const { status, data } = await taskService.attempts.cancel({
      attemptId: attempt.id,
      data: {
        reason,
        attemptId: attempt.id,
        modifiedOn: attempt.modifiedOn,
      },
      toastMessageConfig: {
        success: {
          text: "Attempt Cancelled",
          subtext: `${address} was cancelled`,
        },
      },
    });

    if (status === HTTP_STATUSES.OK) {
      onAttemptUpdated(data);
    }
  };

  const loadCancelReasons = async () => {
    const response = await taskService.reasonGroups.getCancelReasons({});

    if (response.status === HTTP_STATUSES.OK) {
      const { data } = response;
      const reasons = isEmpty(data) ? [{ reason: "None" }] : data;
      setCancelReasons(reasons);
    }
  };

  const loadCannotPerformReasons = async () => {
    return stateManager.run(async () => {
      const response = await taskService.cannotPerformReasons.getAll({
        sort: "label",
      });

      if (response.status === HTTP_STATUSES.OK) {
        const { data } = response;
        const reasons = isEmpty(data) ? [{ label: "None" }] : data;
        setCannotPerformReasons(reasons);
      }
    });
  };

  return {
    isLoading: stateManager.isBusy(),
    cancelReasons,
    cannotPerformReasons,
    handleStartWork,
    handleDispatchWork,
    handleUnschedule,
    handleStop,
    handleSubmitWork,
    handleHold,
    handleLock,
    loadCancelReasons,
    loadCannotPerformReasons,
    handleCancel,
  };
};
